import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";
import { Toast } from "primereact/toast";
import { paymentLinkService } from "../../../services/paymentLinkService";
import { useParams } from "react-router-dom";
import { Button } from "primereact/button";

const POLL_INTERVAL_MS = 5_000;
const POLL_TIMEOUT_MS  = 300_000;

type PageState =
  | "loading"
  | "form"
  | "submitting"
  | "success"
  | "failed"
  | "expired"
  | "paid"
  | "processing"
  | "error";

// ── Composants utilitaires ──────────────────────────────────────────────────

const WacePayBadge = () => (
  <div className="flex items-center gap-2">
    <div className="w-7 h-7 rounded-lg bg-primary-600 flex items-center justify-center shadow-sm">
      <span className="text-white text-xs font-bold">W</span>
    </div>
    <div>
      <p className="text-sm font-semibold text-gray-800 leading-none">Paiement Mobile Money</p>
      <p className="text-xs text-gray-400 mt-0.5">Sécurisé par WacePay</p>
    </div>
    <i className="pi pi-lock text-gray-300 text-xs ml-auto" />
  </div>
);

const StatusScreen = ({
  icon, iconClass, title, message, sub, action,
}: {
  icon: string; iconClass: string; title: string;
  message: string; sub?: string; action?: React.ReactNode;
}) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
    <div className="bg-white rounded-2xl shadow-lg w-full max-w-sm p-8 text-center">
      <WacePayBadge />
      <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mt-6 mb-4 ${iconClass}`}>
        <i className={`${icon} text-2xl`} />
      </div>
      <h2 className="text-xl font-bold text-gray-800 mb-2">{title}</h2>
      <p className="text-gray-500 text-sm mb-2">{message}</p>
      {sub && <p className="text-gray-400 text-xs mb-6">{sub}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  </div>
);

const Spinner = ({ message }: { message: string }) => (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center z-50">
    <div className="w-14 h-14 border-4 border-white/20 border-t-primary-500 rounded-full animate-spin mb-6" />
    <p className="text-white font-medium text-sm">{message}</p>
    <p className="text-white/50 text-xs mt-2">Ne fermez pas cette page</p>
    <p className="text-white/30 text-xs absolute bottom-8">🔒 Sécurisé par WACEPAY</p>
  </div>
);

// ── Carte opérateur ─────────────────────────────────────────────────────────

const OperatorCard = ({
  operator,
  selected,
  onSelect,
  feeBearer,
}: {
  operator: any;
  selected: boolean;
  onSelect: () => void;
  feeBearer: string;
}) => {
  const logoSrc = operator.logoUrl ?? "/assets/images/services/default.png";

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`relative flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all focus:outline-none w-24 ${
        selected
          ? "border-primary-600 bg-primary-50 shadow-md"
          : "border-gray-200 hover:border-gray-300 bg-white"
      }`}
    >
      <img
        src={logoSrc}
        alt={operator.name}
        className="w-12 h-12 rounded-xl object-cover"
        onError={(e) => {
          e.currentTarget.src = "/assets/images/services/default.png";
        }}
      />
      <span className="text-xs text-gray-600 font-medium text-center leading-tight line-clamp-2">
        {operator.name}
      </span>
      {feeBearer === "PAYER" && operator.feeRate > 0 && (
        <span className="text-xs text-orange-500 font-semibold">+{operator.feeRate}%</span>
      )}
      {selected && (
        <span className="absolute -top-2 -right-2 w-5 h-5 bg-primary-600 rounded-full flex items-center justify-center shadow">
          <i className="pi pi-check text-white" style={{ fontSize: "0.6rem" }} />
        </span>
      )}
    </button>
  );
};

// ── Panneau montant ─────────────────────────────────────────────────────────

const AmountPanel = ({
  paymentData,
  selectedOperator,
}: {
  paymentData: any;
  selectedOperator: any | null;
}) => {
  const baseAmount = Number(paymentData?.amount ?? 0);
  const currency   = paymentData?.currency ?? "";
  const feeBearer  = paymentData?.feeBearer ?? "CUSTOMER";

  const feeRate    = feeBearer === "PAYER" && selectedOperator ? Number(selectedOperator.feeRate ?? 0) : 0;
  const feeAmount  = feeBearer === "PAYER" && feeRate > 0 ? Math.ceil(baseAmount * feeRate / 100) : 0;
  const grossAmount = feeBearer === "PAYER" ? Math.ceil(baseAmount + baseAmount * feeRate / 100) : baseAmount;

  const fmt = (n: number) => n.toLocaleString("fr-FR");

  if (feeBearer === "CUSTOMER") {
    return (
      <div className="mt-8">
        <p className="text-xs uppercase tracking-widest text-slate-400 mb-1 font-medium">
          Montant à payer
        </p>
        <div className="mt-2">
          <span className="text-4xl font-bold">{fmt(baseAmount)}</span>
          <span className="text-slate-300 ml-2 text-base font-medium">{currency}</span>
        </div>
        <p className="text-xs text-emerald-400 mt-2 flex items-center gap-1">
          <i className="pi pi-check-circle text-xs" />
          Frais inclus
        </p>
      </div>
    );
  }

  // feeBearer === PAYER
  if (!selectedOperator) {
    return (
      <div className="mt-8">
        <p className="text-xs uppercase tracking-widest text-slate-400 mb-1 font-medium">
          Montant demandé
        </p>
        <div className="mt-2">
          <span className="text-4xl font-bold">{fmt(baseAmount)}</span>
          <span className="text-slate-300 ml-2 text-base font-medium">{currency}</span>
        </div>
        <p className="text-xs text-orange-300 mt-2 flex items-center gap-1">
          <i className="pi pi-info-circle text-xs" />
          Des frais s'ajouteront selon l'opérateur
        </p>
      </div>
    );
  }

  return (
    <div className="mt-8 space-y-2">
      <p className="text-xs uppercase tracking-widest text-slate-400 mb-3 font-medium">
        Détail du paiement
      </p>
      <div className="flex justify-between text-sm text-slate-300">
        <span>Montant demandé</span>
        <span>{fmt(baseAmount)} {currency}</span>
      </div>
      <div className="flex justify-between text-sm text-orange-300">
        <span>Frais ({feeRate}%)</span>
        <span>+ {fmt(feeAmount)} {currency}</span>
      </div>
      <div className="border-t border-slate-600 pt-2 flex justify-between items-baseline">
        <span className="text-xs text-slate-400 uppercase tracking-wide">Total à payer</span>
        <div>
          <span className="text-3xl font-bold text-white">{fmt(grossAmount)}</span>
          <span className="text-slate-300 ml-2 text-sm">{currency}</span>
        </div>
      </div>
    </div>
  );
};

// ── Composant principal ─────────────────────────────────────────────────────

const PaymentLink: React.FC = () => {
  const { id_link } = useParams<{ id_link: string }>();
  const toastRef    = useRef<Toast>(null);

  const [pageState, setPageState]         = useState<PageState>("loading");
  const [paymentData, setPaymentData]     = useState<any>(null);
  const [selected, setSelected]           = useState<string | null>(null);
  const [errorMsg, setErrorMsg]           = useState<string>("");
  const [paidAmount, setPaidAmount]       = useState<number | null>(null);

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimeoutRef  = useRef<ReturnType<typeof setTimeout>  | null>(null);

  const stopPolling = () => {
    if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
    if (pollTimeoutRef.current)  { clearTimeout(pollTimeoutRef.current);   pollTimeoutRef.current  = null; }
  };

  useEffect(() => () => stopPolling(), []);

  const fetchPaymentLink = async () => {
    setPageState("loading");
    if (!id_link) { setPageState("error"); setErrorMsg("Ce lien de paiement est invalide."); return; }

    try {
      const res = await paymentLinkService.initPayment(id_link);

      if (res?.code === "PAYMENT_LINK_EXPIRED" || res?.status === 410) { setPageState("expired"); return; }
      if (res?.status === 409 && res?.data?.status === "PAID")         { setPageState("paid");    return; }
      if (res?.status === 409 && res?.data?.status === "PROCESSING")   { setPageState("processing"); return; }

      if (res?.status !== 2000 || !res?.data) {
        setPageState("error");
        setErrorMsg("Ce lien de paiement n'est pas disponible. Contactez le marchand.");
        return;
      }

      setPaymentData(res.data);
      setPageState("form");
    } catch {
      setPageState("error");
      setErrorMsg("Impossible de charger les détails. Vérifiez votre connexion et réessayez.");
    }
  };

  useEffect(() => { fetchPaymentLink(); }, []);

  const startPollingStatus = () => {
    pollTimeoutRef.current = setTimeout(() => {
      stopPolling();
      setPageState("form");
      toastRef.current?.show({
        severity: "warn",
        summary: "Confirmation en attente",
        detail: "Nous n'avons pas pu confirmer votre paiement. Si vous avez été débité, contactez le marchand.",
        life: 8000,
      });
    }, POLL_TIMEOUT_MS);

    pollIntervalRef.current = setInterval(async () => {
      try {
        const res = await paymentLinkService.PaymentLinkCheckStatus(id_link!);
        const txStatus = res?.data?.transactionStatus;

        if (txStatus === "SUCCESS") { stopPolling(); setPageState("success"); return; }
        if (["FAILED","EXPIRED","CANCELED","ABANDONED"].includes(txStatus)) {
          stopPolling(); setPageState("failed"); return;
        }
      } catch { /* continue silently */ }
    }, POLL_INTERVAL_MS);
  };

  const handleSelect = async (operator: { code: string; id: string }) => {
    setSelected(operator.code);
    await formik.setFieldValue("serviceId", operator.id, false);
    await formik.setFieldValue("selected", operator.code, false);
    await formik.setFieldTouched("selected", true, false);
    await formik.setFieldTouched("serviceId", true, false);
    await formik.validateField("selected");
    await formik.validateField("serviceId");
  };

  const formik = useFormik({
    initialValues: { serviceId: "", phoneNumber: "", selected: "" },
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: Yup.object({
      serviceId:   Yup.string().required("Veuillez sélectionner un opérateur"),
      phoneNumber: Yup.string()
        .required("Le numéro de téléphone est requis")
        .matches(/^\+\d{6,15}$/, "Entrez un numéro international valide"),
      selected:    Yup.string().required("Veuillez sélectionner un opérateur"),
    }),
    onSubmit: async (values) => {
      setPageState("submitting");

      // Calcul du montant réellement débité pour l'écran de succès
      const baseAmount = Number(paymentData?.amount ?? 0);
      const feeBearer  = paymentData?.feeBearer ?? "CUSTOMER";
      const op         = paymentData?.services?.find((s: any) => s.id === values.serviceId);
      const feeRate    = feeBearer === "PAYER" ? Number(op?.feeRate ?? 0) : 0;
      const gross      = feeBearer === "PAYER" ? Math.ceil(baseAmount + baseAmount * feeRate / 100) : baseAmount;
      setPaidAmount(gross);

      try {
        const res = await paymentLinkService.initPaymentLinkTransaction({
          serviceId:   values.serviceId,
          phoneNumber: values.phoneNumber,
          linkId:      id_link!,
        });

        if (res?.code === "PAYMENT_LINK_EXPIRED" || res?.status === 410) { setPageState("expired"); return; }
        if (res?.status === 409) { setPageState(res?.data?.status === "PAID" ? "paid" : "processing"); return; }

        if (res?.status !== 2000) {
          setPageState("form");
          toastRef.current?.show({
            severity: "warn",
            summary: "Paiement non initié",
            detail: "Impossible de traiter votre paiement. Vérifiez vos informations et réessayez.",
            life: 5000,
          });
          return;
        }

        startPollingStatus();
      } catch (err: any) {
        setPageState("form");
        const isValidation = err?.httpStatus >= 400 && err?.httpStatus < 500;
        toastRef.current?.show({
          severity: isValidation ? "warn" : "error",
          summary: isValidation ? "Vérifiez vos informations" : "Erreur de connexion",
          detail: isValidation
            ? err?.message ?? "Veuillez vérifier vos informations et réessayer."
            : "Une erreur réseau est survenue. Réessayez.",
          life: 6000,
        });
      }
    },
  });

  // ── Écrans d'état ───────────────────────────────────────────────────────────

  if (pageState === "loading") return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-gray-200 border-t-primary-500 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-gray-500 text-sm">Chargement…</p>
      </div>
    </div>
  );

  if (pageState === "error") return (
    <StatusScreen icon="pi pi-exclamation-circle" iconClass="bg-red-100 text-red-500"
      title="Lien indisponible" message={errorMsg}
      action={<Button label="Réessayer" icon="pi pi-refresh" className="p-button-outlined" onClick={fetchPaymentLink} />}
    />
  );

  if (pageState === "expired") return (
    <StatusScreen icon="pi pi-clock" iconClass="bg-orange-100 text-orange-500"
      title="Lien expiré" message="Ce lien de paiement n'est plus valide."
      sub="Les liens expirent après 15 minutes. Contactez le marchand pour en obtenir un nouveau."
    />
  );

  if (pageState === "paid") return (
    <StatusScreen icon="pi pi-check-circle" iconClass="bg-green-100 text-green-600"
      title="Déjà payé" message="Ce paiement a déjà été effectué avec succès."
      sub="Vous n'avez pas besoin de payer à nouveau."
    />
  );

  if (pageState === "processing") return (
    <StatusScreen icon="pi pi-spin pi-spinner" iconClass="bg-blue-100 text-blue-500"
      title="Paiement en cours" message="Un paiement est en cours de traitement pour ce lien."
      sub="Patientez et actualisez la page pour vérifier le statut."
      action={<Button label="Actualiser" icon="pi pi-refresh" className="p-button-outlined" onClick={fetchPaymentLink} />}
    />
  );

  if (pageState === "success") return (
    <StatusScreen icon="pi pi-check-circle" iconClass="bg-green-100 text-green-600"
      title="Paiement réussi"
      message={`Votre paiement de ${Number(paidAmount ?? paymentData?.amount).toLocaleString("fr-FR")} ${paymentData?.currency} à ${paymentData?.merchantName} a été effectué.`}
      sub="Vous recevrez une confirmation sous peu."
    />
  );

  if (pageState === "failed") return (
    <StatusScreen icon="pi pi-times-circle" iconClass="bg-red-100 text-red-500"
      title="Paiement échoué" message="Votre paiement n'a pas pu être finalisé."
      sub="Réessayez ou utilisez un autre opérateur."
      action={
        <Button label="Réessayer" icon="pi pi-refresh"
          onClick={() => { formik.resetForm(); setSelected(null); setPaidAmount(null); setPageState("form"); }}
        />
      }
    />
  );

  // ── Formulaire principal ────────────────────────────────────────────────────

  const country = (paymentData?.customerCountry || "CM").toUpperCase() as any;
  const canSubmit = formik.isValid && !!formik.values.serviceId && !!formik.values.phoneNumber && pageState !== "submitting";

  const selectedOperator = paymentData?.services?.find((s: any) => s.code === selected) ?? null;
  const feeBearer        = paymentData?.feeBearer ?? "CUSTOMER";
  const baseAmount       = Number(paymentData?.amount ?? 0);
  const feeRate          = feeBearer === "PAYER" && selectedOperator ? Number(selectedOperator.feeRate ?? 0) : 0;
  const grossAmount      = feeBearer === "PAYER" ? Math.ceil(baseAmount + baseAmount * feeRate / 100) : baseAmount;
  const displayAmount    = selectedOperator ? grossAmount : baseAmount;

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4 py-8">
      <Toast ref={toastRef} position="top-center" />
      {pageState === "submitting" && <Spinner message="Traitement de votre paiement…" />}

      <div className="w-full max-w-2xl rounded-2xl overflow-hidden shadow-xl flex flex-col sm:flex-row">

        {/* ── Panneau gauche — récapitulatif ── */}
        <div className="bg-slate-800 text-white p-8 flex flex-col justify-between sm:w-64 flex-shrink-0">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-400 mb-6 font-medium">
              Résumé du paiement
            </p>

            <div className="space-y-4">
              <div>
                <p className="text-xs text-slate-400">Marchand</p>
                <p className="font-semibold text-sm mt-0.5">{paymentData?.merchantName}</p>
              </div>
              {paymentData?.reason && (
                <div>
                  <p className="text-xs text-slate-400">Motif</p>
                  <p className="text-sm mt-0.5 text-slate-200">{paymentData.reason}</p>
                </div>
              )}
            </div>

            <AmountPanel paymentData={paymentData} selectedOperator={selectedOperator} />
          </div>

          <div className="mt-8 flex items-center gap-2 bg-slate-700/60 rounded-lg px-3 py-2">
            <i className="pi pi-lock text-emerald-400 text-xs" />
            <span className="text-xs text-slate-300">Transaction chiffrée 256-bit</span>
          </div>
        </div>

        {/* ── Panneau droit — formulaire ── */}
        <div className="bg-white flex-1 p-8 flex flex-col">

          {/* Header */}
          <div className="mb-6 pb-5 border-b border-gray-100">
            <WacePayBadge />
          </div>

          <form onSubmit={formik.handleSubmit} className="flex flex-col gap-6 flex-1">

            {/* Sélection opérateur */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">
                Opérateur mobile money <span className="text-red-500">*</span>
              </p>
              {feeBearer === "PAYER" && (
                <p className="text-xs text-orange-500 mb-3 flex items-center gap-1">
                  <i className="pi pi-info-circle text-xs" />
                  Les frais de plateforme sont à votre charge — le total varie selon l'opérateur choisi.
                </p>
              )}

              {!paymentData?.services?.length ? (
                <div className="text-center py-6 text-gray-400 text-sm bg-gray-50 rounded-xl">
                  <i className="pi pi-info-circle block text-2xl mb-2" />
                  Aucun opérateur disponible pour ce lien.
                </div>
              ) : (
                <div className="flex flex-wrap gap-3">
                  {paymentData.services.map((operator: any) => (
                    <OperatorCard
                      key={operator.id}
                      operator={operator}
                      selected={selected === operator.code}
                      onSelect={() => handleSelect(operator)}
                      feeBearer={feeBearer}
                    />
                  ))}
                </div>
              )}

              {formik.errors.selected && formik.touched.selected && (
                <p className="text-red-500 text-xs mt-2 flex items-center gap-1">
                  <i className="pi pi-exclamation-circle text-xs" />
                  {formik.errors.selected}
                </p>
              )}
            </div>

            {/* Numéro mobile money */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                Numéro mobile money <span className="text-red-500">*</span>
              </p>
              <PhoneInput
                placeholder="Entrez votre numéro"
                international
                countryCallingCodeEditable={false}
                id="phoneNumber"
                name="phoneNumber"
                defaultCountry={country}
                countries={[country]}
                className={`w-full px-3 py-2.5 border rounded-xl text-sm bg-white ${
                  formik.errors.phoneNumber && formik.touched.phoneNumber
                    ? "border-red-400 bg-red-50"
                    : "border-gray-200 focus-within:border-primary-400"
                }`}
                value={formik.values.phoneNumber}
                onBlur={formik.handleBlur}
                onChange={(phone) => {
                  formik.setFieldValue("phoneNumber", phone || "", true);
                  formik.setFieldTouched("phoneNumber", true, true);
                }}
              />
              {formik.errors.phoneNumber && formik.touched.phoneNumber && (
                <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <i className="pi pi-exclamation-circle text-xs" />
                  {formik.errors.phoneNumber}
                </p>
              )}
            </div>

            {/* Bouton payer */}
            <div className="mt-auto">
              <button
                type="submit"
                disabled={!canSubmit}
                className={`w-full py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all ${
                  canSubmit
                    ? "bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}
              >
                {pageState === "submitting" ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Traitement…
                  </>
                ) : (
                  <>
                    <i className="pi pi-send text-sm" />
                    Payer {Number(displayAmount).toLocaleString("fr-FR")} {paymentData?.currency}
                    {feeBearer === "PAYER" && !selectedOperator && (
                      <span className="text-white/60 text-xs font-normal">+ frais</span>
                    )}
                  </>
                )}
              </button>

              <p className="text-center text-xs text-slate-400 mt-4 border-b border-gray-200 pb-4">
                Ce lien expire 15 minutes après sa création
              </p>

              <p className="text-center text-xs text-gray-400 mt-3 flex items-center justify-center gap-1">
                <i className="pi pi-lock text-xs" />
                Paiement sécurisé par WacePay
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PaymentLink;
